import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { DatePicker, Drawer, Flex, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { api, unwrap } from "@/api/client";
import { ExportButton } from "@/components/list/ExportButton";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { NoWms, useWmsWarehouse } from "./common";

const DAY = "YYYY-MM-DD";
type Row = Awaited<ReturnType<typeof fetchPerformance>>["rows"][number];

const fetchPerformance = (warehouseId: string, query: { from: string; to: string; by?: "day"; userId?: string }) =>
  unwrap(api.GET("/v1/warehouses/{warehouseID}/staff-performance", { params: { path: { warehouseID: warehouseId }, query } }));

/**
 * Performa staf: what each member did in the active warehouse over a period,
 * from what the warehouse records anyway. A member's row opens their days.
 * Staff without warehouse.manage see their own row only.
 */
export function StaffPerformance() {
  const { t } = useI18n();
  const { can } = useAuth();
  const { current, loading } = useWmsWarehouse();
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => [dayjs().subtract(6, "day"), dayjs()]);
  const [member, setMember] = useState<Row | null>(null);
  const query = { from: range[0].format(DAY), to: range[1].format(DAY) };
  const list = useQuery({
    queryKey: ["staff-performance", current?.id, query],
    queryFn: () => fetchPerformance(current!.id, query),
    enabled: !!current,
    placeholderData: keepPreviousData,
  });
  const days = useQuery({
    queryKey: ["staff-performance", current?.id, query, member?.userId],
    queryFn: () => fetchPerformance(current!.id, { ...query, by: "day", userId: member!.userId }),
    enabled: !!current && member !== null,
  });
  if (!loading && !current) return <NoWms />;
  const cols = columns(t);

  return (
    <>
      <Flex justify="space-between" align="baseline" wrap gap={12} style={{ marginBottom: 8 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("perf.title")}
        </Typography.Title>
        <Typography.Text type="secondary">{current?.name}</Typography.Text>
      </Flex>
      <Typography.Paragraph type="secondary" style={{ maxWidth: 820 }}>
        {t(can("warehouse.manage") ? "perf.help" : "perf.helpSelf")}
      </Typography.Paragraph>
      <Flex gap={12} wrap style={{ marginBottom: 16 }} align="center">
        <DatePicker.RangePicker value={range} allowClear={false} onChange={(v) => v && v[0] && v[1] && setRange([v[0], v[1]])} format="DD/MM/YYYY" />
        <div style={{ flex: 1 }} />
        {current && (
          <ExportButton
            path={`/v1/warehouses/${current.id}/staff-performance/export`}
            query={query}
            filename={`${t("perf.title")} ${query.from} ${query.to}`}
            columns={cols.map((c) => ({ key: c.key as string, label: c.label, kind: c.key === "name" ? undefined : ("number" as const) }))}
          />
        )}
      </Flex>
      <Table<Row>
        rowKey="userId"
        loading={list.isFetching && !list.data}
        dataSource={list.data?.rows ?? []}
        pagination={false}
        scroll={{ x: 1300 }}
        locale={{ emptyText: t("perf.empty") }}
        onRow={(r) => ({ onClick: () => setMember(r), style: { cursor: "pointer" } })}
        columns={tableColumns(cols, "name")}
      />
      <Drawer open={member !== null} onClose={() => setMember(null)} size="large" title={member ? t("perf.daysOf", { name: member.name }) : ""} destroyOnHidden>
        <Table<Row>
          rowKey="day"
          size="small"
          loading={days.isPending}
          dataSource={days.data?.rows ?? []}
          pagination={false}
          scroll={{ x: 1200 }}
          columns={tableColumns(cols, "day")}
        />
      </Drawer>
    </>
  );
}

type Col = { key: keyof Row; label: string; help?: string };

function columns(t: ReturnType<typeof useI18n>["t"]): Col[] {
  return [
    { key: "name", label: t("perf.member") },
    { key: "pickTasks", label: t("perf.pickTasks") },
    { key: "pickUnits", label: t("perf.pickUnits") },
    { key: "pickPerHour", label: t("perf.pickPerHour"), help: t("perf.perHourHelp") },
    { key: "putawayTasks", label: t("perf.putawayTasks") },
    { key: "putawayUnits", label: t("perf.putawayUnits") },
    { key: "putawayPerHour", label: t("perf.putawayPerHour"), help: t("perf.perHourHelp") },
    { key: "packed", label: t("perf.packed") },
    { key: "handed", label: t("perf.handed") },
    { key: "received", label: t("perf.received") },
    { key: "counted", label: t("perf.counted") },
    { key: "shortUnits", label: t("perf.short"), help: t("perf.shortHelp") },
  ];
}

/** The table's columns; the first is the member, or the day in their drawer. */
function tableColumns(cols: Col[], first: "name" | "day"): ColumnsType<Row> {
  return cols.map((c, i) =>
    i === 0
      ? {
          key: first,
          dataIndex: first,
          title: first === "day" ? "" : c.label,
          width: first === "day" ? 120 : 200,
          fixed: "left" as const,
          render: (v: string) => (first === "day" ? <span className="z-num">{dayjs(v).format("ddd DD/MM")}</span> : <Typography.Text strong>{v}</Typography.Text>),
        }
      : {
          key: c.key,
          dataIndex: c.key,
          title: c.help ? <span title={c.help}>{c.label}</span> : c.label,
          width: 110,
          align: "right" as const,
          render: (v: number | null) => <span className="z-num">{v == null || v === 0 ? "—" : v.toLocaleString("id-ID")}</span>,
        },
  );
}
